/**
 * Economics Input Component
 *
 * Input fields for electricity cost and uptime with compact design.
 */

import { Box, TextField, MenuItem, FormControl, Select, Grid, Typography } from '@mui/material';
import type { EconomicsParameters } from '../../types';

interface EconomicsProps {
  value: EconomicsParameters;
  onChange: (value: EconomicsParameters) => void;
}

export function EconomicsInput({ value, onChange }: EconomicsProps) {
  const handleChange = (field: keyof EconomicsParameters) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValue = Number(event.target.value);
    onChange({ ...value, [field]: newValue });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Section Header */}
      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
        Economics Parameters
      </Typography>

      {/* Horizontal Layout: Electricity + Uptime */}
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
            Electricity ($/kWh)
          </Typography>
          <TextField
            type="number"
            value={value.electricityRate}
            onChange={handleChange('electricityRate')}
            inputProps={{ min: 0, max: 1, step: 0.01, style: { fontSize: '0.8rem' } }}
            fullWidth
            size="small"
            sx={{ '& .MuiInputBase-input': { py: 0.5 } }}
          />
        </Grid>

        <Grid item xs={6}>
          <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
            Uptime (hrs/day)
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={value.uptimeHoursPerDay}
              onChange={(e) => onChange({ ...value, uptimeHoursPerDay: Number(e.target.value) as number })}
              sx={{ fontSize: '0.8rem' }}
            >
              <MenuItem value={8} sx={{ fontSize: '0.8rem' }}>8 hrs</MenuItem>
              <MenuItem value={12} sx={{ fontSize: '0.8rem' }}>12 hrs</MenuItem>
              <MenuItem value={16} sx={{ fontSize: '0.8rem' }}>16 hrs</MenuItem>
              <MenuItem value={24} sx={{ fontSize: '0.8rem' }}>24/7</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
        Adjust electricity rate for your region and expected server uptime
      </Typography>
    </Box>
  );
}
